"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { usePageTransition } from "@/lib/page-transition-context";
import type { Post } from "@/lib/posts";
import { BlogCard } from "./BlogCard";

const DRAG_SENS = 0.55;
const DRAG_THRESHOLD = 6;
const SNAP_MS = 520;
const AUTO_DEG_PER_FRAME = 0.028;
const MAX_CARD_WIDTH = 200;
const MIN_CARD_WIDTH = 150;
const CARD_ASPECT = 360 / 228;
/** Neighbor edge gap when the ring is densely packed (many posts). */
const CARD_GAP = 52;
/**
 * Shared cylinder diameter so JDL (fewer posts) matches JDI’s arc.
 * Without this, radius shrinks with post count and the orbit looks flatter.
 */
const MIN_ORBIT_RADIUS = 480;
const MAX_RADIUS = 520;
const RING_TILT = 6;

type OrbitLayout = {
  radius: number;
  cardWidth: number;
  cardHeight: number;
};

/**
 * Keep a consistent orbit diameter across sites. Prefer CARD_GAP when there
 * are many posts; never let fewer posts collapse the ring below MIN_ORBIT_RADIUS.
 */
function orbitLayout(count: number): OrbitLayout {
  if (count <= 1) {
    return {
      radius: 0,
      cardWidth: MAX_CARD_WIDTH,
      cardHeight: Math.round(MAX_CARD_WIDTH * CARD_ASPECT),
    };
  }
  const halfStep = Math.PI / count;
  const sinHalf = Math.sin(halfStep);
  let cardWidth = MAX_CARD_WIDTH;
  let radius = Math.ceil((cardWidth + CARD_GAP) / (2 * sinHalf));
  radius = Math.max(radius, MIN_ORBIT_RADIUS);

  if (radius > MAX_RADIUS) {
    radius = MAX_RADIUS;
    const chord = 2 * radius * sinHalf;
    cardWidth = Math.floor(chord - CARD_GAP);
    if (cardWidth < MIN_CARD_WIDTH) {
      cardWidth = MIN_CARD_WIDTH;
      radius = Math.max(
        MIN_ORBIT_RADIUS,
        Math.ceil((cardWidth + CARD_GAP) / (2 * sinHalf)),
      );
    }
  }

  return {
    radius,
    cardWidth,
    cardHeight: Math.round(cardWidth * CARD_ASPECT),
  };
}

type Props = {
  posts: Post[];
  label?: string;
};

function mod(n: number, m: number): number {
  return ((n % m) + m) % m;
}

export function BlogCarousel({ posts, label = "Blog posts" }: Props) {
  const navigate = usePageTransition();
  const count = posts.length;
  const step = count > 0 ? 360 / count : 0;
  const { radius, cardWidth, cardHeight } = orbitLayout(count);

  const [rotation, setRotation] = useState(0);
  const [paused, setPaused] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [animating, setAnimating] = useState(false);

  const rotationRef = useRef(0);
  const dragRef = useRef({
    active: false,
    pointerId: -1,
    startX: 0,
    startRot: 0,
    moved: false,
  });
  const reduceMotion = useRef(false);
  const rafRef = useRef<number | null>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);

  const activeIndex = mod(Math.round(-rotation / step), count || 1);

  const applyRotation = useCallback((deg: number, animate = false) => {
    rotationRef.current = deg;
    setRotation(deg);
    setAnimating(animate);
  }, []);

  const snapTo = useCallback(
    (index: number, animate = true) => {
      if (!count) return;
      const target = -mod(index, count) * step;
      applyRotation(target, animate);
    },
    [applyRotation, count, step],
  );

  useEffect(() => {
    reduceMotion.current = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 860px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (paused || reduceMotion.current || count <= 1) return;

    const tick = () => {
      if (dragRef.current.active) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      applyRotation(rotationRef.current - AUTO_DEG_PER_FRAME, false);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [paused, dragging, count, applyRotation]);

  useEffect(() => {
    if (!animating) return;
    const id = window.setTimeout(() => setAnimating(false), SNAP_MS);
    return () => window.clearTimeout(id);
  }, [animating, rotation]);

  const finishDrag = useCallback(
    (openFrontOnTap = true) => {
      if (!dragRef.current.active) return;
      const moved = dragRef.current.moved;
      dragRef.current.active = false;
      dragRef.current.pointerId = -1;
      setDragging(false);

      const snapped = Math.round(rotationRef.current / step) * step;
      applyRotation(snapped, true);

      if (openFrontOnTap && !moved && count > 0) {
        const index = mod(Math.round(-snapped / step), count);
        navigate(`/blogs/${posts[index].slug}`);
      }
    },
    [applyRotation, count, navigate, posts, step],
  );

  useEffect(() => {
    if (isMobile) return;

    const onPointerMove = (e: PointerEvent) => {
      if (!dragRef.current.active || e.pointerId !== dragRef.current.pointerId) {
        return;
      }
      e.preventDefault();
      const dx = e.clientX - dragRef.current.startX;
      if (Math.abs(dx) > DRAG_THRESHOLD) dragRef.current.moved = true;
      applyRotation(dragRef.current.startRot + dx * DRAG_SENS, false);
    };

    const onPointerEnd = (e: PointerEvent) => {
      if (!dragRef.current.active || e.pointerId !== dragRef.current.pointerId) {
        return;
      }
      stageRef.current?.releasePointerCapture(e.pointerId);
      finishDrag();
    };

    window.addEventListener("pointermove", onPointerMove, { passive: false });
    window.addEventListener("pointerup", onPointerEnd);
    window.addEventListener("pointercancel", onPointerEnd);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerEnd);
      window.removeEventListener("pointercancel", onPointerEnd);
    };
  }, [applyRotation, finishDrag, isMobile]);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (isMobile || count <= 1 || e.button !== 0) return;
    if ((e.target as Element).closest(".blog-card")) return;
    e.preventDefault();
    dragRef.current = {
      active: true,
      pointerId: e.pointerId,
      startX: e.clientX,
      startRot: rotationRef.current,
      moved: false,
    };
    setDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  function go(next: number) {
    snapTo(next, true);
  }

  const controls = (
    <div className="blog-carousel-controls">
      <button
        type="button"
        className="blog-carousel-arrow"
        aria-label="Previous post"
        onClick={() => go(activeIndex - 1)}
      >
        ‹
      </button>
      <div className="blog-carousel-dots" role="tablist" aria-label="Posts">
        {posts.map((p, i) => (
          <button
            key={p.slug}
            type="button"
            role="tab"
            aria-selected={i === activeIndex}
            aria-label={`Go to ${p.title}`}
            className={`blog-carousel-dot${i === activeIndex ? " is-active" : ""}`}
            onClick={() => go(i)}
          />
        ))}
      </div>
      <button
        type="button"
        className="blog-carousel-arrow"
        aria-label="Next post"
        onClick={() => go(activeIndex + 1)}
      >
        ›
      </button>
    </div>
  );

  if (count === 0) return null;

  if (count === 1) {
    return (
      <div className="blog-orbit blog-orbit-single" aria-label={label}>
        <BlogCard post={posts[0]} />
      </div>
    );
  }

  return (
    <div
      className={`blog-orbit${dragging ? " is-dragging" : ""}${animating ? " is-snapping" : ""}${isMobile ? " is-mobile" : ""}`}
      style={{
        ["--orbit-item-w" as string]: `${cardWidth}px`,
        ["--orbit-item-h" as string]: `${cardHeight}px`,
        ["--orbit-radius" as string]: `${radius}px`,
        ["--orbit-stage-h" as string]: `${Math.max(460, cardHeight + 140)}px`,
        ["--orbit-perspective" as string]: `${Math.max(1100, Math.round(radius * 2.1))}px`,
        ["--orbit-tilt" as string]: `${isMobile ? 4 : RING_TILT}deg`,
      }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setPaused(false);
      }}
      aria-roledescription="carousel"
      aria-label={label}
    >
      {/* CSS shows this on ≤860px; 3D stage is display:none — no JS gate for Lighthouse. */}
      <div className="blog-orbit-mobile-card">
        <BlogCard post={posts[activeIndex]} />
      </div>

      <div
        ref={stageRef}
        className="blog-orbit-stage"
        onPointerDown={isMobile ? undefined : onPointerDown}
        aria-hidden={isMobile ? true : undefined}
      >
        <div className="blog-orbit-floor" aria-hidden />
        <div
          className="blog-orbit-ring"
          style={{
            transform: `rotateX(var(--orbit-tilt)) rotateY(${rotation}deg)`,
          }}
        >
          {posts.map((post, i) => {
            const itemAngle = i * step;
            const faceAngle = -(itemAngle + rotation);
            const dist = Math.abs(mod(itemAngle + rotation + 180, 360) - 180);
            // Hide the back half so mirrored backs never show
            if (dist > 95) return null;
            const focus = Math.max(0.72, 1 - dist / 130);
            const scale = 1;
            const isFront = i === activeIndex;
            return (
              <div
                key={post.slug}
                className={`blog-orbit-item${isFront ? " is-front" : ""}`}
                style={{
                  width: cardWidth,
                  height: cardHeight,
                  marginLeft: -cardWidth / 2,
                  marginTop: -cardHeight / 2,
                  transform: `rotateY(${itemAngle}deg) translateZ(${radius}px) scale(${scale})`,
                  opacity: focus,
                  zIndex: Math.round(focus * 100),
                }}
                // inert (not aria-hidden): excludes from a11y/focus without
                // nesting focusable links under aria-hidden (axe aria-hidden-focus).
                inert={!isFront}
              >
                <div
                  className="blog-orbit-face"
                  style={{ transform: `rotateY(${faceAngle}deg)` }}
                >
                  <BlogCard
                    post={post}
                    compactDate
                    interactive={isFront}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {controls}
    </div>
  );
}
